import ReactGA from 'react-ga4';

import { getAnalyticsPath } from './productAnalytics';

const MEASUREMENT_ID = 'G-4TYYK84XM4';

// GA4 reads attribution from these parameters on page_location. Every other
// query parameter can hold a token or a Stripe session ID, so it is dropped.
const CAMPAIGN_PARAMETERS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
  'gclid',
  'gbraid',
  'wbraid',
  'dclid',
];

export type CartinesePlan = 'API_SUBSCRIPTION' | 'LIFETIME' | 'SUBSCRIPTION';

const PLAN_ITEMS: Record<CartinesePlan, { item_id: string; item_name: string }> = {
  API_SUBSCRIPTION: { item_id: 'cartinese_api', item_name: 'Cartinese API' },
  LIFETIME: { item_id: 'cartinese_lifetime', item_name: 'Cartinese Lifetime' },
  SUBSCRIPTION: { item_id: 'cartinese_premium', item_name: 'Cartinese Premium' },
};

const planItem = (plan: CartinesePlan, price?: number) => ({
  ...PLAN_ITEMS[plan],
  item_category: 'Premium',
  ...(price === undefined ? {} : { price }),
  quantity: 1,
});

export const initializeGoogleAnalytics = () => {
  // RouteChangeTracker sends every page_view, including the first one.
  ReactGA.initialize(MEASUREMENT_ID, { gtagOptions: { send_page_view: false } });
};

let lastPagePath = '';

export const trackGooglePageView = (pathname: string, search: string, title: string) => {
  const path = getAnalyticsPath(pathname);
  // A query-string change keeps the normalized path, so it is not a new page.
  if (path === lastPagePath) return;
  lastPagePath = path;

  const sourceParameters = new URLSearchParams(search);
  const campaignParameters = new URLSearchParams();
  CAMPAIGN_PARAMETERS.forEach((name) => {
    const value = sourceParameters.get(name);
    if (value) campaignParameters.set(name, value);
  });
  const query = campaignParameters.toString();
  const location = `${window.location.origin}${path}${query ? `?${query}` : ''}`;

  // Without this, gtag reads document.location for every later event on the page.
  ReactGA.set({ page_location: location, page_title: title });
  ReactGA.send({ hitType: 'pageview', location, title });
};

export const trackViewCartinesePlans = (subscriptionPrice: number, apiPrice: number) => {
  ReactGA.event('view_item', {
    currency: 'USD',
    items: [planItem('SUBSCRIPTION', subscriptionPrice), planItem('API_SUBSCRIPTION', apiPrice)],
  });
};

export const trackBeginCheckout = (plan: CartinesePlan, price?: number) => {
  ReactGA.event('begin_checkout', {
    currency: 'USD',
    items: [planItem(plan, price)],
    ...(price === undefined ? {} : { value: price }),
  });
};

export const trackCheckoutFailed = () => {
  ReactGA.event('exception', { description: 'checkout_session_failed', fatal: false });
};

// A trial checkout charges 0. The backend hashes the Checkout session ID into
// transaction_id, because the raw ID can activate premium for another account.
export const trackPurchase = (purchase: {
  amount: number;
  currency: string;
  is_trial: boolean;
  subscription_type: CartinesePlan;
  transaction_id: string;
}) => {
  ReactGA.event('purchase', {
    currency: purchase.currency,
    items: [{
      ...planItem(purchase.subscription_type, purchase.amount),
      item_variant: purchase.is_trial ? 'trial' : 'paid',
    }],
    transaction_id: purchase.transaction_id,
    value: purchase.amount,
  });
};
